import { daysUntil, formatBillingDate } from './billingEntitlements';

const DEFAULT_BILLING_CURRENCY = 'USD';
const MONTHLY_PRICE_DOLLARS = 7;
export const BILLING_SUPPORT_EMAIL = 'support@myvylo.com';

const normalizeCurrencyCode = (currency = DEFAULT_BILLING_CURRENCY) => (
  String(currency || DEFAULT_BILLING_CURRENCY).trim().toUpperCase() || DEFAULT_BILLING_CURRENCY
);

export function getMonthlyPriceDisplay(currency = DEFAULT_BILLING_CURRENCY) {
  return `$${MONTHLY_PRICE_DOLLARS} ${normalizeCurrencyCode(currency)}/month plus applicable taxes`;
}

export function getMonthlyPriceCtaDisplay(currency = DEFAULT_BILLING_CURRENCY) {
  return `$${MONTHLY_PRICE_DOLLARS} ${normalizeCurrencyCode(currency)}/month + tax`;
}

export function getOutstandingBalanceDisplay(currency = DEFAULT_BILLING_CURRENCY) {
  return `$${MONTHLY_PRICE_DOLLARS} ${normalizeCurrencyCode(currency)}`;
}

export function getPaidIntroOfferCopy(offer) {
  const currency = normalizeCurrencyCode(offer?.currency);
  const introUnitAmount = Number(offer?.introductory?.unitAmount);
  const recurringUnitAmount = Number(offer?.recurring?.unitAmount);
  const format = (unitAmount) => new Intl.NumberFormat(
    currency === 'CAD' ? 'en-CA' : 'en-US',
    {
      style: 'currency',
      currency,
      minimumFractionDigits: unitAmount % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }
  ).format(unitAmount / 100);

  if (!Number.isFinite(introUnitAmount) || !Number.isFinite(recurringUnitAmount)) {
    return null;
  }

  const introPrice = format(introUnitAmount);
  const recurringPrice = format(recurringUnitAmount);
  return {
    title: `Your first month for ${introPrice}`,
    subtitle: `Then ${recurringPrice} ${currency}/month, plus applicable taxes. Cancel anytime.`,
    cta: `Continue for ${introPrice}`,
    disclosure: `${introPrice} ${currency} plus applicable taxes is charged today. Renews at ${recurringPrice} ${currency}/month plus applicable taxes until canceled.`,
    steps: [
      `${introPrice} for your first month`,
      `${recurringPrice} ${currency}/month after that`,
      'Cancel anytime in Billing',
    ],
  };
}

const normalizeBillingErrorText = (error) => [
  error?.code,
  error?.decline_code,
  error?.type,
  error?.status,
  error?.message,
  error?.data?.code,
  error?.data?.error,
]
  .filter(Boolean)
  .join(' ')
  .toLowerCase();

export function getBillingFailureCopy(error) {
  const errorText = normalizeBillingErrorText(error);
  const status = Number(error?.status || error?.response?.status || 0);

  if (
    errorText.includes('card_declined') ||
    errorText.includes('declined') ||
    errorText.includes('insufficient_funds') ||
    errorText.includes('do_not_honor') ||
    errorText.includes('lost_card') ||
    errorText.includes('stolen_card')
  ) {
    return {
      headline: 'That card was declined.',
      subheadline: 'Nothing was charged. Try another card or contact your bank.',
    };
  }

  if (
    errorText.includes('authentication') ||
    errorText.includes('verification') ||
    errorText.includes('requires_action') ||
    errorText.includes('setup_intent_authentication_failure')
  ) {
    return {
      headline: 'Card verification didn’t finish.',
      subheadline: 'Nothing was charged. Complete verification or try another card.',
    };
  }

  if (
    status >= 500 ||
    errorText.includes('network') ||
    errorText.includes('failed to fetch') ||
    errorText.includes('timeout') ||
    errorText.includes('temporarily unavailable')
  ) {
    return {
      headline: 'We couldn’t reach billing.',
      subheadline: 'Nothing changed. Try again in a moment.',
    };
  }

  return {
    headline: 'We couldn’t save that payment method.',
    subheadline: 'Nothing was charged. Check the details or try another card.',
  };
}

function formatTrialCountdown(daysLeft) {
  if (daysLeft === 0) return 'today';
  if (daysLeft === 1) return '1 day';
  if (typeof daysLeft === 'number' && Number.isFinite(daysLeft)) return `${daysLeft} days`;
  return null;
}

export function getPaymentMethodUpdatedSubtitle(status, currency = 'USD') {
  const billingStartDate = formatBillingDate(status?.trialEndsAt || status?.currentPeriodEnd);
  const price = getMonthlyPriceDisplay(currency);
  const isTrialing =
    status?.billingState === 'trialing_no_payment_method' ||
    status?.billingState === 'trialing_with_payment_method';

  if (isTrialing) {
    const countdown = formatTrialCountdown(status?.trialDaysRemaining ?? daysUntil(status?.trialEndsAt));

    if (countdown && billingStartDate) {
      return countdown === 'today'
        ? `Your free trial ends today. You'll be charged ${price} starting ${billingStartDate}.`
        : `Your free trial ends in ${countdown}. You'll be charged ${price} starting ${billingStartDate}.`;
    }

    if (billingStartDate) {
      return `Your free trial ends on ${billingStartDate}. You'll be charged ${price} starting ${billingStartDate}.`;
    }
  }

  if (billingStartDate) {
    return `You'll be charged ${price}, starting on ${billingStartDate}.`;
  }

  return `You'll be charged ${price} once your current trial ends.`;
}
